import { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import Raven from 'raven-js';
import { useGetSettingsQuery } from '../../__generated__/chirp/com/hubspot/commerce/buyer/portal/rpc/PublicPortalSettingsRpc.hooks';
import { ErrorActions } from '../constants/ErrorActions';
import { Metrics } from '../utils/Metrics';
import { useAppSelector } from '../redux';
import { selectIsAuthenticated } from '../features/AuthSlice/AuthSelectors';
import { jsx as _jsx } from "react/jsx-runtime";
const PublicPortalSettingsContext = /*#__PURE__*/createContext(null);
export function PublicPortalSettingsProvider({
  children
}) {
  const startTimeRef = useRef(null);
  const updateFetchDurationMetric = () => {
    if (startTimeRef.current !== null) {
      Metrics.timer('portal-settings-fetch-duration').update(performance.now() - startTimeRef.current);
      startTimeRef.current = null;
    }
  };
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const {
    data,
    loading,
    error
  } = useGetSettingsQuery({
    skip: !isAuthenticated,
    onCompleted() {
      updateFetchDurationMetric();
      Metrics.counter('portal-settings-fetch-success').increment();
    },
    onError(fetchError) {
      updateFetchDurationMetric();
      Metrics.counter('portal-settings-fetch-failure').increment();
      Raven.captureException(fetchError, {
        tags: {
          action: ErrorActions.PORTAL_SETTINGS_FETCHED
        }
      });
    }
  });
  useEffect(() => {
    if (loading && startTimeRef.current === null) {
      startTimeRef.current = performance.now();
    }
  }, [loading]);
  const value = useMemo(() => {
    const errorMessage = error ? error instanceof Error ? error.message : String(error) : null;
    return {
      portalSettings: data !== null && data !== void 0 ? data : null,
      loading,
      error: errorMessage
    };
  }, [data, error, loading]);
  return /*#__PURE__*/_jsx(PublicPortalSettingsContext.Provider, {
    value: value,
    children: children
  });
}
export function usePublicPortalSettings() {
  const context = useContext(PublicPortalSettingsContext);
  if (!context) {
    throw new Error('usePublicPortalSettings must be used within PublicPortalSettingsProvider');
  }
  return context;
}